import React from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import {
  ScreenLink,
  useScreenDispatch,
  useScreenGo,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import {
  AffiliateFormCreate,
  AffiliateFormEdit,
} from 'scenes/Affiliates/components/AffiliateForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_PARTNER_COMPANY,
  COLUMN_BUSINESS_PARTNER,
  COLUMN_NETWORK,
  COLUMN_TRAFFIC_TYPES,
  COLUMN_EXPENSE_TYPE,
  COLUMN_OWNER,
  COLUMN_CREATED_BY,
  COLUMN_UPDATED_DATE,
  COLUMN_CHANNEL,
  COLUMN_ADDITIONAL_FEE,
  COLUMN_CREATED_DATE,
  TAB_ACTIONS_LOG,
} from 'scenes/Affiliates/constants';
import { getColor } from '../Status';
import {
  useLoadAffiliateGrid,
  useIsUpdating,
  useExportAffiliates,
} from 'scenes/Affiliates/data';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/Affiliates/screenReducer';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';
import PartnerCompanyDetails from 'scenes/PartnerCompanies/components/PartnerCompanyDetails';

import DataGrid from '@gogame/data-grid';
import { affiliatesDetailsLink } from '../../navigation';

import AffiliateActions from '../Actions';
import AffiliatesToolbar from './Toolbar';

const AffiliateDetailsLink = ({ children, row }) => {
  return (
    <ScreenLink link={affiliatesDetailsLink} pathParams={{ id: row.id }}>
      {children}
    </ScreenLink>
  );
};

const AffiliateDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery } = screenState;
  const params = { filters, tableSettings, searchQuery };

  const { data, isLoading, isRefetching } = useLoadAffiliateGrid(params);
  const isUpdating = useIsUpdating();
  const [exportAffiliates] = useExportAffiliates();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(AffiliateFormEdit, { id });
  };

  const handleOpenPartnerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanyDetails, { id });
  };

  const handleOpenBusinessPartner = (_e, { id }) => {
    openChildComponent(BusinessPartnerDetails, { id });
  };
  const handleExport = async () => {
    await exportAffiliates(params);
  };
  const go = useScreenGo();

  const handleShowActionLog = ({ id }) => {
    go(id, {
      state: { activeTabId: TAB_ACTIONS_LOG },
    });
  };

  const handleCreate = () => {
    openChildComponent(AffiliateFormCreate);
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching || isUpdating}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={
          <AffiliatesToolbar onExport={handleExport} onCreate={handleCreate} />
        }
        storageKey={'affiliates'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={88}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          type={'link'}
          field={COLUMN_NAME}
          headerName={'Name'}
          width={280}
          linkComponent={AffiliateDetailsLink}
          getTooltip={(v) => v}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={116}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER_COMPANY}
          headerName={'Partner Company'}
          width={210}
          type={'link'}
          onLinkClick={handleOpenPartnerCompany}
          getTooltip={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNER}
          headerName={'Business Partner'}
          width={210}
          type={'link'}
          onLinkClick={handleOpenBusinessPartner}
          getTooltip={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_NETWORK}
          headerName={'Network'}
          width={120}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_CHANNEL}
          headerName={'Channel'}
          width={120}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_TRAFFIC_TYPES}
          headerName={'Traffic Types'}
          width={120}
          type={'text'}
        />

        <DataGrid.Column
          field={COLUMN_EXPENSE_TYPE}
          headerName={'Expense Type'}
          width={120}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_ADDITIONAL_FEE}
          headerName={'Additional Spend Fee'}
          width={144}
          type={'percent'}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          headerName={'Owner'}
          width={160}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />

        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={160}
          type={'text'}
        />

        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          field={'actions'}
          type={'actions'}
          actionsComponent={AffiliateActions}
          onEdit={handleEdit}
          onViewLog={handleShowActionLog}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default AffiliateDataTable;
